import * as React from "react";
import { useHistory, useParams } from "react-router";

import { SocialNetworkProfiles } from "@/components/SocialNetworkProfiles";
import { PageWrapper } from "@/components/PageWrapper";
import { CoachSettings, SettingsTags } from "@/interfaces";
import {
  useSocialCoachSession,
  usePlayerSettings,
  redirectPlayerIfSignUpNotFinished,
  URLS,
  getPlayerAccountFromSession,
} from "@/utils";

import { PlayerMyCustomizations } from "@/containers/Players/Customizations";
import { VideoPageThemes } from "@/containers/VideoPages/VideoPageThemes";
import { Tabs } from "@/components/ui/tabs";
import { PlayerProfileHeader } from "./components/PlayerProfileHeader";

import { useMemo } from "react";
import { SocialCoachSession } from "@/services/bedrock";

interface SettingMenu {
  title: string;
  activeSection: SettingsTags;
}
interface OwnProps {
  successHandler: (session: SocialCoachSession) => void;
}

type Props = OwnProps;

const IntegratedPlayerSettings: React.FC<Props> = () => {
  const history = useHistory();
  const params = useParams<{ tab?: string }>();
  const { playerId, session, refreshSession } = useSocialCoachSession();
  const { hasSetting } = usePlayerSettings(playerId);
  const hasMVPThemeAccess = useMemo(() => {
    return hasSetting(CoachSettings.PLAYER_MVP_ACCESS) && hasSetting(CoachSettings.PLAYER_MVP_THEMES);
  }, [hasSetting]);

  React.useEffect(() => {
    redirectPlayerIfSignUpNotFinished(session);
  }, [session]);

  const initialTab = (params["tab"] as SettingsTags) || "social-networks";

  const [activeSection, setActiveSection] = React.useState<SettingsTags>(initialTab);

  React.useEffect(() => {
    history.replace(URLS.player.settings.replace(":tab?", activeSection));
  }, [activeSection]);

  const playerAccount = useMemo(() => getPlayerAccountFromSession(session), [session]);

  const menuList: SettingMenu[] = useMemo(() => {
    const list: SettingMenu[] = [
      {
        title: "Social Accounts",
        activeSection: "social-networks",
      },
      {
        title: "Customizations",
        activeSection: "customizations",
      },
    ];
    if (hasMVPThemeAccess) {
      list.push({
        title: "Video Page Themes",
        activeSection: "mvp",
      });
    }
    return list;
  }, [hasMVPThemeAccess]);

  const selectedContent = React.useMemo(() => {
    switch (activeSection) {
      case "social-networks":
        return <SocialNetworkProfiles />;
      case "customizations":
        return <PlayerMyCustomizations />;
      case "mvp":
        return <VideoPageThemes />;
      default:
        return null;
    }
  }, [activeSection]);

  return (
    <PageWrapper title="Settings" hideSeparator>
      <div className="flex flex-col gap-6">
        <PlayerProfileHeader playerAccount={playerAccount} fetching={!session} onSuccess={refreshSession} />

        <Tabs value={activeSection} onValueChange={val => setActiveSection(val as SettingsTags)}>
          <Tabs.List variant="menu" className="mb-2">
            {menuList.map(element => (
              <Tabs.Trigger key={element.activeSection} value={element.activeSection}>
                {element.title}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs>

        <div className="w-full">{selectedContent}</div>
      </div>
    </PageWrapper>
  );
};

export const PlayerSettings = IntegratedPlayerSettings;
